import { ResourceType } from "@evergis/api";
import { Description, Dropdown, Icon, IconButton, IOption, Popover, useToggle } from "@evergis/ui";
import React, { FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { getAppConfig } from "../../../../ducks/appConfig";
import { useNotification } from "../../../../hooks/notifications/useNotification";
import {
  ExternalLink,
  LinkDescription,
  LinkPanelRow,
  LinkText,
  PublicLink,
  PublicLinkPanel,
  PublicLinkPanelControls,
  PublicLinkTextControls,
} from "./styled";
import { CopyPublicResourceLinkProps, ResourceLinkType, SocialItem } from "./types";
import { SocialType } from "../../../../appConfig/types";

export const SOCIAL_ITEMS: Record<SocialType, SocialItem> = {
  [SocialType.VK]: {
    icon: "social_vk",
    shareLink: "https://vk.com/share.php?url=",
  },
  [SocialType.Telegram]: {
    icon: "social_tg",
    shareLink: "https://t.me/share/url?url=",
  },
};

export const DEFAULT_ALLOWED_SOCIALS = [SocialType.VK, SocialType.Telegram];

const getResourceLink = (name: string, resourceType: ResourceType, linkType: ResourceLinkType) => {
  // eslint-disable-next-line default-case
  switch (linkType) {
    case ResourceLinkType.Public:
      return resourceType === ResourceType.Project
        ? `${location.origin}/shared/${name}/layers`
        : `${location.origin}/shared/layers/${name}`;
    case ResourceLinkType.WMS:
      return `${location.origin}/sp/wms/${name}`;
    case ResourceLinkType.Developers:
      return resourceType === ResourceType.Project
        ? `${location.origin}/sp/projects/${name}`
        : `${location.origin}/sp/layers/${name}`;
  }
};

// eslint-disable-next-line max-lines-per-function
export const CopyPublicResourceLink: FC<CopyPublicResourceLinkProps> = ({ name, resourceType, isPublicRole }) => {
  const { t } = useTranslation("dialogs");

  const appConfig = useSelector(getAppConfig);
  const shareSocials = appConfig?.settings?.shareSocials;
  const shareSocialsAllowed = Array.isArray(shareSocials) ? shareSocials : DEFAULT_ALLOWED_SOCIALS;
  const shareSocialItems = useMemo(
    () =>
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      shareSocialsAllowed.reduce<SocialItem[]>(
        (acc, socialType) => (SOCIAL_ITEMS[socialType] ? [...acc, SOCIAL_ITEMS[socialType]] : acc),
        [],
      ),
    [shareSocialsAllowed],
  );

  const [notificationTrigger, setNotificationTrigger] = useState(0);
  const [opened, toggleOpened] = useToggle();
  const [currentLinkType, setCurrentLinkType] = useState(
    resourceType === ResourceType.Project && isPublicRole ? ResourceLinkType.Public : ResourceLinkType.WMS,
  );

  const linkTypesOptions = useMemo(
    () =>
      [
        resourceType === ResourceType.Project &&
          isPublicRole && {
            text: t("access.publicLink"),
            value: ResourceLinkType.Public,
          },
        {
          text: "WMS",
          value: ResourceLinkType.WMS,
        },
        {
          text: t("access.developersLink"),
          value: ResourceLinkType.Developers,
        },
      ].filter(Boolean),
    [t, resourceType, isPublicRole],
  );

  const handleChange = useCallback(([{ value }]: IOption[]) => {
    setCurrentLinkType(value as ResourceLinkType);
  }, []);

  useNotification(notificationTrigger, {
    success: true,
    title: t("access.linkCopied"),
    description: "",
  });

  const link = getResourceLink(name, resourceType, currentLinkType);

  return (
    <Popover
      anchor={<PublicLink onClick={toggleOpened}>{t("access.copyPublicLink")}</PublicLink>}
      open={opened}
      onRequestClose={toggleOpened}
      anchorOrigin="bottom-left"
      targetOrigin="top-left"
      zIndex={1000}
    >
      <PublicLinkPanel>
        <LinkPanelRow>
          <Dropdown
            options={linkTypesOptions}
            width={"8.75rem"}
            onChange={handleChange}
            value={currentLinkType}
            zIndex={1010}
          />
          <PublicLinkTextControls>
            <LinkText>{link}</LinkText>
            <IconButton
              kind="copy"
              onClick={() => {
                navigator.clipboard.writeText(link);
                setNotificationTrigger(notificationTrigger + 1);
              }}
            />
          </PublicLinkTextControls>
          <PublicLinkPanelControls>
            {shareSocialItems.map(({ icon, shareLink }) => (
              <IconButton key={icon} kind={icon} onClick={() => window.open(`${shareLink}${link}`, "_blank")} />
            ))}
          </PublicLinkPanelControls>
        </LinkPanelRow>
        <LinkPanelRow>
          <Icon style={{ marginRight: "0.5rem" }} kind="warning" />
          <Description>
            {t("access.activationInfo1")}
            <br />
            {t("access.activationInfo2")}
          </Description>
        </LinkPanelRow>
        <LinkPanelRow>
          <ExternalLink href={`${location.origin}/help/sharing`} target="_blank">
            <Icon style={{ marginRight: "0.5rem" }} kind="external_link" primary />
            <LinkDescription>{t("access.linkTypesInfo")}</LinkDescription>
          </ExternalLink>
        </LinkPanelRow>
      </PublicLinkPanel>
    </Popover>
  );
};
